/**
 * Ion basis-reload guard — a tiny coordination primitive between
 * commandLayer.js and filterStore.js.
 *
 * Hotfix (Ion-as-Insights P1, Task 10 follow-up): "self-degradation via async
 * basis reload". commandLayer.js's provenance-degradation effect nulls
 * commandProvenance whenever a RANGE_SIGNAL changes outside a command-layer
 * call (commandDepth === 0) — that's how a genuine manual filter change
 * invalidates a stale Revert. But a basis-changing Ion command (applyContextCommand,
 * or applyFilterCommand's range-basis-switch branch) also triggers
 * FilterPanel.jsx's `useEffect(..., [basis.value, domain.value])`, which
 * fetches fresh metadata and calls filterStore.js's
 * initFilterRangesFromMetadata() — an ASYNC call that lands well after the
 * Ion command's own call stack has unwound (commandDepth back to 0), writing
 * every RANGE_SIGNAL straight. Without this guard the degradation effect
 * can't tell that reload-triggered write from a genuine manual one, and
 * wipes the just-committed attribution card.
 *
 * This file has NO other imports so importing it from both commandLayer.js
 * and filterStore.js doesn't create a circular dependency between those two
 * (much larger) modules — commandLayer.js sets the guard the instant it
 * changes basis/domain; filterStore.js clears it as the LAST step of
 * initFilterRangesFromMetadata, once that reload has made all of its writes.
 * This couples the suppression window to the real reload boundary, not a
 * timer, and self-heals: a reload that never fires (metadata fetch failed,
 * or nothing consumes it) just leaves future range writes suppressed until
 * the next reload actually runs and clears it — see task-10-report.md for the
 * one known edge case this implies.
 */

let expectingReload = false

/** Called by commandLayer.js the instant an Ion command changes basis/domain. */
export function markIonBasisReloadExpected() {
  expectingReload = true
}

/**
 * Called by filterStore.js's initFilterRangesFromMetadata once it has
 * finished writing this reload's ranges. Idempotent / safe to call
 * unconditionally — a no-op when no reload was expected (e.g. the initial
 * mount, or a reload triggered by something other than an Ion command).
 */
export function consumeIonBasisReloadExpectation() {
  expectingReload = false
}

/** Read-only check used by commandLayer.js's degradation effect. */
export function isIonBasisReloadExpected() {
  return expectingReload
}
